import { AfterViewInit, ChangeDetectorRef, Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { FeedbackProviderTagScore } from '@app/app/shared/models';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ChartContent, ChartDataLabels, ChartItem, ChartVersionScore } from '../../../../models/editor/chart-content.model';

@Component({
  selector: 'ptl-radar-chart-preview',
  templateUrl: './radar-chart-preview.component.html',
  styleUrls: ['./radar-chart-preview.component.scss'],
})
export class RadarChartPreviewComponent implements OnInit, AfterViewInit {
  @Input() includeSelfFeedback: boolean;
  @Input() chartContent: ChartContent;
  @Input() selectedUserScoreVersion: number;

  @ViewChild('Chart', { read: BaseChartDirective }) chartComponent: BaseChartDirective;

  chartId = new Date().getTime();
  chartData: ChartDataset[] = [];
  chartLabels: string[];
  chartOptions: ChartConfiguration['options'];
  chartDataLabels: ChartDataLabels[] = [];
  activeLabelIndex: number | null = null;

  protected readonly DEFAULT_USER_RESULT_COLOR = '#BFBFFF';
  protected readonly DEFAULT_PROVIDER_RESULT_COLOR = '#D1EFD8';

  private readonly BASE_OFFSET = 20;
  private readonly DIAGONAL_OFFSET = 20;
  private filteredChartItems: ChartItem[] = [];
  private feedbackDataset: ChartDataset;

  constructor(
    private cd: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    if (this.chartContent) {
      this.filteredChartItems = this.chartContent.items.filter((item) => item.scored !== null);
      this.setChartOptions();
      this.initializeChartData();
    }
  }

  ngAfterViewInit(): void {
    this.zone.onStable.subscribe(() => this.calculateLabelPositions());
    window.addEventListener('resize', () => this.calculateLabelPositions());
  }

  togglePopup(index: number) {
    this.activeLabelIndex = this.activeLabelIndex === index ? null : index;
  }

  userResultColor(alpha?: number): string {
    if (alpha) {
      return this.getTransparentColor(this.chartContent?.radarChartColorScheme?.selfRatingColor ?? this.DEFAULT_USER_RESULT_COLOR, alpha);
    }
    return this.chartContent?.radarChartColorScheme?.selfRatingColor ?? this.DEFAULT_USER_RESULT_COLOR;
  }

  providerResultColor(alpha?: number): string {
    if (alpha) {
      return this.getTransparentColor(
        this.chartContent?.radarChartColorScheme?.feedbackProvidersColor ?? this.DEFAULT_PROVIDER_RESULT_COLOR,
        alpha,
      );
    }
    return this.chartContent?.radarChartColorScheme?.feedbackProvidersColor ?? this.DEFAULT_PROVIDER_RESULT_COLOR;
  }

  getTransparentColor(hex: string, alpha: number): string {
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  updateChartDataByDates(): void {
    const selectedVersionScore = this.chartContent?.versionedScores.find((s) => s.version === this.selectedUserScoreVersion);
    if (!selectedVersionScore) return;

    const userDataset = this.buildUserDataset(selectedVersionScore, this.filteredChartItems);
    this.chartData = this.includeSelfFeedback ? this.sortDatasetsBySize([userDataset, this.feedbackDataset]) : [this.feedbackDataset];
    this.calculateLabelPositions();
  }

  private buildUserDataset(scores: ChartVersionScore, items: ChartItem[]): ChartDataset {
    const userDataset = this.createEmptyRadarDataset();

    items.forEach((item) => {
      const scoreItem = scores.scores.find((s) => s.userScore !== null && item.itemUid === s.tagId);
      if (scoreItem) {
        const userScore = Math.floor((scoreItem.userScore * 100) / scoreItem.maxScore);
        this.pushScore(userDataset, Math.min(userScore, 100), this.userResultColor(), 0.6);
      }
    });

    return userDataset;
  }

  private buildFeedbackDataset(items: ChartItem[]): ChartDataset {
    const dataset = this.createEmptyRadarDataset();

    items.forEach((item) => {
      const avg = this.getAvgFeedbackScorePercentage(item.feedbackProviderTagScores ?? [], item.total);
      this.pushScore(dataset, Math.min(avg, 100), this.providerResultColor(), 0.6);
    });

    return dataset;
  }

  private calculateLabelPositions() {
    const chart = this.chartComponent?.chart;
    if (!chart) return;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const scale = chart.scales.r as any;
    if (!scale) return;

    const centerX = scale.xCenter;
    const centerY = scale.yCenter;
    const radius = scale.drawingArea;

    const items = this.filteredChartItems;

    this.chartDataLabels = items.map((item, i) => {
      const angle = i * ((2 * Math.PI) / items.length) - Math.PI / 2;
      const diagFactor = Math.abs(Math.sin(angle)) * Math.abs(Math.cos(angle));
      const offset = this.BASE_OFFSET + diagFactor * this.DIAGONAL_OFFSET;

      const x = centerX + Math.cos(angle) * (radius + offset);
      const y = centerY + Math.sin(angle) * (radius + offset);
      const transform = this.getAnchorTransform(angle);

      const avgFeedbackScore = this.getAvgFeedbackScore(item.feedbackProviderTagScores ?? []);
      const avgFeedbackScorePercent = this.getAvgFeedbackScorePercentage(item.feedbackProviderTagScores ?? [], item.total);

      const selectedVersionScore = this.chartContent.versionedScores.find((version) => version.version === this.selectedUserScoreVersion);

      const userScoreItem = selectedVersionScore?.scores.find((s) => s.userScore !== null && s.tagId === item.itemUid);

      const userRawScore = userScoreItem?.userScore ?? 0;
      const userMaxScore = userScoreItem?.maxScore ?? 0;
      const userScorePercent = userMaxScore ? Math.floor((userRawScore * 100) / userMaxScore) : 0;

      return {
        label: item.title,
        left: x,
        top: y,
        transform: transform,
        feedbackProviderTagScores: item.feedbackProviderTagScores ?? [],
        allFeedbackProvidersAvgScore: avgFeedbackScorePercent,
        avgRawScore: avgFeedbackScore,
        userScorePercent: userScorePercent,
        userRawScore: userRawScore,
        userMaxScore: userMaxScore,
        totalPossible: item.total,
      } as ChartDataLabels;
    });

    this.cd.detectChanges();
  }

  private initializeChartData(): void {
    this.chartLabels = this.filteredChartItems.map((item) => item.title);
    const [userDataset, feedbackDataset] = this.buildDatasets(this.chartContent.versionedScores[0], this.filteredChartItems);
    this.feedbackDataset = feedbackDataset;
    this.chartData = this.includeSelfFeedback ? this.sortDatasetsBySize([userDataset, feedbackDataset]) : [feedbackDataset];
  }

  private buildDatasets(latestScores: ChartVersionScore, contentItems: ChartItem[]): ChartDataset[] {
    const userDataset = this.buildUserDataset(latestScores, contentItems);
    const feedbackDataset = this.buildFeedbackDataset(contentItems);
    return [userDataset, feedbackDataset];
  }

  private createEmptyRadarDataset(): ChartDataset {
    return {
      data: [],
      backgroundColor: [],
      hoverBackgroundColor: [],
      borderColor: [],
      borderWidth: 1,
      pointRadius: 0,
      pointHoverRadius: 0,
    };
  }

  private pushScore(dataset: ChartDataset, value: number, hexColor: string, alpha: number): void {
    dataset.data.push(value);
    dataset.backgroundColor = this.hexToRgba(hexColor, alpha);
    dataset.borderColor = this.hexToRgba(hexColor, 1);
    dataset.hoverBackgroundColor = this.hexToRgba(hexColor, 1);
  }

  private hexToRgba(hex: string, alpha: number): string {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private setChartOptions(): void {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      layout: {
        padding: {
          top: 5,
          bottom: 5,
        },
      },
      scales: {
        r: {
          min: 0,
          max: 100,
          pointLabels: {
            display: false,
          },
          ticks: {
            stepSize: 20,
            font: {
              size: 10,
            },
            callback: (v) => `${v}%`,
            backdropColor: 'rgba(0, 0, 0, 0)',
            display: true,
            padding: 3,
            z: 1,
          },
          grid: {
            color: 'rgb(166, 166, 166, 0.75)',
            lineWidth: 1,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    };
  }

  private getAnchorTransform(angle: number): string {
    let deg = (angle * 180) / Math.PI;
    if (deg < 0) deg += 360;

    // Get a value from -1 (left) to 1 (right)
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);

    let xAnchor = '-50%'; // Center
    let yAnchor = '-50%'; // Center

    // Adjust x anchor
    if (cosA > 0.6)
      xAnchor = '0%'; // Right
    else if (cosA < -0.6) xAnchor = '-100%'; // Left

    // Adjust y anchor
    if (sinA > 0.6)
      yAnchor = '0%'; // Bottom
    else if (sinA < -0.6) yAnchor = '-100%'; // Top

    return `translate(${xAnchor}, ${yAnchor})`;
  }

  private getAvgFeedbackScore(scores: FeedbackProviderTagScore[] = []): number {
    const validScores = scores.filter((s) => typeof s.tagScore === 'number').map((s) => s.tagScore);
    if (validScores.length === 0) return 0;

    const sum = validScores.reduce((total, score) => total + score, 0);
    return sum / validScores.length;
  }

  private getAvgFeedbackScorePercentage(scores: FeedbackProviderTagScore[] = [], total: number): number {
    if (!total) return 0;
    const validScores = scores.filter((s) => typeof s.tagScore === 'number').map((s) => s.tagScore);
    if (validScores.length === 0) return 0;

    const percentages = validScores.map((score) => (score * 100) / total);
    const sumPercentages = percentages.reduce((t, p) => t + p, 0);
    return Math.round(sumPercentages / percentages.length);
  }

  private sortDatasetsBySize(datasets: ChartDataset[]): ChartDataset[] {
    if (!datasets || datasets.length <= 1) {
      return datasets || [];
    }

    return datasets.sort((a, b) => {
      const avgA = this.calculateDatasetAverage(a);
      const avgB = this.calculateDatasetAverage(b);
      return avgA - avgB;
    });
  }

  private calculateDatasetAverage(dataset: ChartDataset): number {
    if (!dataset.data || dataset.data.length === 0) return 0;
    const numericData = dataset.data.filter((value): value is number => typeof value === 'number');
    if (numericData.length === 0) return 0;
    const sum = numericData.reduce((total, value) => total + value, 0);
    return sum / numericData.length;
  }
}
